import React from 'react';
import { type AITrainingData } from '@/services/ai-training.service';
import { Brain, Edit, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

interface TrainingDataTableProps {
  entries: AITrainingData[];
  isLoading: boolean;
  onEdit: (item: AITrainingData) => void;
  onDelete: (item: AITrainingData) => void;
  onToggleStatus: (item: AITrainingData) => void;
  isStatusPending: boolean;
  getCategoryLabel: (category: string) => string;
}

const textPreviewClass = 'whitespace-pre-wrap break-words [overflow-wrap:anywhere]';

export const TrainingDataTable = ({
  entries,
  isLoading,
  onEdit,
  onDelete,
  onToggleStatus,
  isStatusPending,
  getCategoryLabel,
}: TrainingDataTableProps) => {
  const { t } = useTranslation();
  if (isLoading) {
    return (
      <div className="rounded-xl border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5 p-10 text-center">
        <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100">
          <Brain className="h-5 w-5 animate-pulse text-slate-400" />
        </div>
        <p className="text-sm font-medium text-slate-700">{t('common:x2.loadingTrainingData', 'Trainingsdaten werden geladen')}</p>
        <p className="mt-1 text-xs text-slate-500">{t('common:x2.listWillRefresh', 'Die Liste wird gleich aktualisiert.')}</p>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
        <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100">
          <Brain className="h-5 w-5 text-slate-400" />
        </div>
        <p className="text-sm font-semibold text-slate-900">{t('common:x2.noTrainingData', 'Keine Trainingsdaten gefunden')}</p>
        <p className="mt-1 text-sm text-slate-500">
          {t('common:x2.adjustFiltersOrCreate', 'Passen Sie die Filter an oder legen Sie einen neuen Trainingssatz an.')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {entries.map((item) => (
        <article
          key={item.id}
          className="group overflow-hidden rounded-xl border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5 transition-colors hover:border-slate-300"
        >
          <div className="grid gap-0 xl:grid-cols-[minmax(230px,0.75fr)_minmax(0,1.45fr)_180px]">
            <div className="min-w-0 border-b border-slate-100 p-4 xl:border-b-0 xl:border-r">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="truncate text-sm font-semibold text-slate-950">{item.title}</h3>
                  {item.description && (
                    <p className={cn('mt-1 line-clamp-3 text-xs leading-5 text-slate-500', textPreviewClass)}>
                      {item.description}
                    </p>
                  )}
                </div>
                <Badge
                  className={cn(
                    'shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold',
                    item.priority > 5
                      ? 'border-red-200 bg-red-50 text-red-700'
                      : 'border-slate-200 bg-slate-100 text-slate-600',
                  )}
                >
                  P{item.priority}
                </Badge>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Badge
                  variant="secondary"
                  className="rounded-full border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600"
                >
                  {getCategoryLabel(item.category)}
                </Badge>
                <span
                  className={cn(
                    'rounded-full border px-2 py-0.5 text-[10px] font-semibold',
                    item.is_active
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                      : 'border-slate-200 bg-slate-50 text-slate-500',
                  )}
                >
                  {item.is_active ? 'Aktiv' : 'Inaktiv'}
                </span>
              </div>
            </div>

            <div className="min-w-0 space-y-3 border-b border-slate-100 p-4 xl:border-b-0 xl:border-r">
              <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
                    {t('common:x2.instruction', 'Anweisung')}
                  </p>
                </div>
                <p className={cn('line-clamp-5 text-xs leading-5 text-slate-700', textPreviewClass)}>
                  {item.training_prompt}
                </p>
              </div>

              {(item.example_input || item.example_output) && (
                <div className="grid gap-2 md:grid-cols-2">
                  {item.example_input && (
                    <div className="min-w-0 rounded-lg border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5 p-3">
                      <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
                        Input
                      </p>
                      <p className={cn('line-clamp-3 text-xs leading-5 text-slate-500', textPreviewClass)}>
                        {item.example_input}
                      </p>
                    </div>
                  )}
                  {item.example_output && (
                    <div className="min-w-0 rounded-lg border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5 p-3">
                      <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
                        Output
                      </p>
                      <p className={cn('line-clamp-3 text-xs leading-5 text-slate-500', textPreviewClass)}>
                        {item.example_output}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex min-w-0 items-center justify-between gap-3 p-4 xl:flex-col xl:items-stretch xl:justify-center">
              <div className="flex items-center gap-2 xl:justify-between">
                <span className="text-xs font-medium text-slate-500">{t('common:field.status', 'Status')}</span>
                <Switch
                  checked={item.is_active}
                  onCheckedChange={() => onToggleStatus(item)}
                  disabled={isStatusPending}
                  className="data-[state=checked]:bg-[#0f172a]"
                />
              </div>
              <div className="flex items-center justify-end gap-1 xl:pt-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onEdit(item)}
                  className="h-9 w-9 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-950"
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onDelete(item)}
                  className="h-9 w-9 rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </article>
      ))}
    </div>
  );
};
